import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import cn from 'classnames';
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import DocumentLink from '@components/DocumentLink';
import DropshippingModalWindow from '../modals/DropshippingModalWindow/DropshippingModalWindow';
import InfoIcon from '@assets/icons/info_blue_19.svg';
import LinkIcon from '@assets/icons/link.svg';
import SellersBeforeTable from '@components/SellersBeforeTable/SellersBeforeTable';
import TableSelectionPanel from '@components/Table/TableSelectionPanel.tsx';
import { AddSellerPanel } from '@components/AddSeller';
import { BeforeProps } from '@containers/DataView';
import { Button, ButtonSize } from '@components/Button';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate } from '@utils/formatValues';
import { getCurrentLanguage, Langs } from '@utils/lang';
import { getNew, TNewParcel } from '@api/apiClient/dropshipping';
import { INewTabProps } from '../types';
import { Row, Column, Grid } from '@components/Grid';
import { TableColumn, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextType, TextSize, TextWeight } from '@components/Text';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const urlPrefix = 'https://unitrade.fra1.digitaloceanspaces.com/public/';

const instruction = {
  [Langs.ru]: {
    fileSize: '261 KB',
    url: `${urlPrefix}docx/inst_dropshipping_ru.docx`
  },
  [Langs.cn]: {
    fileSize: '532 KB',
    url: `https://storage.yandexcloud.net/unitrade-public/public/180425.pdf`
  },
  [Langs.tr]: null,
  [Langs.en]: null
};

const TopPanel = () => {
  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();

  return (
      <Grid>
        <Row>
          <Box pb={5}>
            <Row gap={6}>
              {instruction[currentLanguage] && (
                  <DocumentLink
                      icon={InfoIcon}
                      text={t('dropshipping.instructionLink')}
                      {...(instruction[currentLanguage] || { url: '', fileSize: '' })}
                  />
              )}
            </Row>
          </Box>
        </Row>
      </Grid>
  );
};

const NewTab = (props: INewTabProps) => {
  const {
    handlerOnUpdate,
    isFilterOpen,
    setIsFilterOpen
  } = props;

  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenDropshippingWindow, setIsOpenDropshippingWindow] = useState<boolean>(false);
  const [linkedItems, setLinkedItems] = useState<string[]>([]);
  const [selectedExternalNumbers, setSelectedExternalNumbers] = useState<string[]>([]);
  const [selectedParcels, setSelectedParcels] = useState<Map<string, TNewParcel>>(new Map());
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const dataRef = useRef<DataViewHandle>(null);
  const { sellersNotFound } = useSellersCounter();
  const { t } = useTranslation();

  const handleSelectionChange = useCallback((
      selectedIds: string[],
      items: TNewParcel[]
  ) => {
    setSelectedRowIds(selectedIds);

    const parcels: Map<string, TNewParcel> = new Map();

    selectedParcels.forEach((parcel: TNewParcel, key: string) => {
      const item = items.find((v: TNewParcel) => v.id === key);
      if (!item) parcels.set(key, parcel);
    });

    selectedIds.forEach((id) => {
      const item = items.find((v: TNewParcel) => (v.id === id));
      if (item) {
        parcels.set(id, item);
      } else {
        const selectedItem = selectedParcels.get(id);
        if (selectedItem) {
          parcels.set(id, selectedItem);
        }
      }
    });

    const externalNumbers = Array.from(parcels, ([_, value]) => value).map((item: TNewParcel) => item.externalId);

    setSelectedExternalNumbers(externalNumbers);
    setSelectedParcels(parcels);
  }, [selectedParcels]);

  const refreshData = () => {
    if (dataRef.current?.refresh) {
      dataRef.current.refresh();
    }
    handlerOnUpdate();
    setSelectedExternalNumbers([]);
    setSelectedParcels(new Map());
    setSelectedRowIds([]);
  };

  const BeforeTableComponent = useCallback((props: BeforeProps<TNewParcel>) => (
      <SellersBeforeTable title={t('dropshipping.beforeTable.title')} {...props} />
  ), []);

  const columns: TableColumn<TNewParcel>[] = [
    {
      header: '',
      accessor: (row: TableRowType<TNewParcel>) => {
        const imgUrl = row.data.items?.[0]?.imageUrl;
        return (
            <span className={styles.imageWrapper}>
              {imgUrl && (
                  <img
                      className={styles.productImage}
                      src={imgUrl}
                      alt="Image"
                  />
              )}
            </span>
        );
      },
      fixWidth: true,
      noOverflow: true,
      width: '50px'
    },
    {
      header: t('dropshipping.newTab.name'),
      accessor: (row: TableRowType<TNewParcel>) => {
        const firstItem = row.data.items?.[0];
        return firstItem && (
            <a
                href={firstItem.url ?? ''}
                target="_blank"
                rel="noreferrer"
            >
              {firstItem.name ?? ''}
            </a>
        );
      },
      fixWidth: true,
      width: '235px'
    },
    {
      header: t('dropshipping.newTab.orderNumberOzon'),
      accessor: (row: TableRowType<TNewParcel>) => (
          <>
            <CopyTextToClipboard
                text={row.data?.externalId}
                visibleOnHover
            >
              <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.S}
                  weight={TextWeight.BOLD}
              >
                {row.data?.externalId}
              </Text>
            </CopyTextToClipboard>
          </>
      ),
      noOverflow: true
    },
    {
      header: t('dropshipping.newTab.seller'),
      accessor: (row: TableRowType<TNewParcel>) => (
          <AvatarWithMarketplace
              id={row.data.seller?.id || ''}
              marketplace="Ozon"
              name={row.data.seller?.name || ''}
              noHover
              type="small"
          />
      ),
      alignment: 'center',
      fixWidth: true,
      noOverflow: true,
      width: '115px'
    },
    {
      header: t('dropshipping.newTab.orderDate'),
      accessor: (row: TableRowType<TNewParcel>) => (
        <div>
          <Text
            color={TextColor.PRIMARY}
            size={TextSize.S}
            type={TextType.BLOCK}
          >
            {formatDate(row.data.createdAt, 'dd.MM.yy')}
          </Text>
          <Text
            color={TextColor.DISABLED}
            size={TextSize.S}
            type={TextType.BLOCK}
          >
            {formatDate(row.data.createdAt, 'HH:mm')}
          </Text>
        </div>
      ),
      fixWidth: true,
      width: '85px'
    },
    {
      header: '',
      accessor: (row: TableRowType<TNewParcel>) => (
          !row.isSelected && (
              <Button
                  onClick={() => {
                    setIsOpenDropshippingWindow(true);
                    setLinkedItems([row.data.externalId]);
                  }}
                  size={ButtonSize.XS}
                  isRounded
                  title={t('dropshipping.newTab.createLink')}
              />
          )
      ),
      alignment: 'center',
      fixWidth: true,
      noOverflow: true,
      width: '115px'
    }
  ];

  if (sellersNotFound) {
    return (
        <>
          <TopPanel/>
          <AddSellerPanel
              isFirstSeller
              isOzonSeller
              onSuccess={refreshData}
          />
        </>
    );
  }

  return (
      <>
        <TopPanel/>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('dropshipping.newTab.description')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="dropshipping-new"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'ExternalIds',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('dropshipping.newTab.orderNumberOzon')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('dropshipping.newTab.orderDate'),
                      fieldNames: ['startDate', 'endDate']
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <TableSelectionPanel
            isShown={selectedRowIds.length > 0}
            items={[
              {
                label: t('dropshipping.newTab.selected'),
                value: selectedRowIds.length
              }
            ]}
            buttons={[
              <Button
                  key="linkToOrder"
                  icon={LinkIcon}
                  onClick={() => {
                    setIsOpenDropshippingWindow(true);
                    setLinkedItems(selectedExternalNumbers);
                  }}
                  size={ButtonSize.S}
                  title={t('dropshipping.newTab.linkToOrder')}
              />
            ]}
        >
          <DataTable<TNewParcel>
              Before={BeforeTableComponent}
              columns={columns}
              dataRef={dataRef}
              defaultPageSize={10}
              filters={innerFilters}
              getData={getNew}
              notFound={t('dropshipping.noParcels')}
              onSelectionChange={handleSelectionChange}
              pending={!isInitialized}
              queryKey="dropshippingNewParcels"
              selectable
              selectedIds={selectedRowIds}
          />
        </TableSelectionPanel>
        {isOpenDropshippingWindow ? (
          <DropshippingModalWindow
              calledFrom="new"
              handlerRefreshData={refreshData}
              onClose={() => setIsOpenDropshippingWindow(false)}
              linkedItems={linkedItems}
          />
        ) : null}
      </>
  );
};

export default NewTab;
